/**
 * Position → communicational-facts mapping (design D9; av-methodology-classification spec).
 *
 * `recommendations.json`'s rule engine is keyed on abstract facts
 * (`soundFunction`, `latency`, `fidelity`) that the 2010 thesis does not use
 * as a classification axis — they are a 2026 interpretive layer, confirmed
 * against the thesis in design.md D9. This module is therefore the mapping
 * design D9/task 4.3 identified as missing: it derives those facts from each
 * historical (2010) matrix position's own `sonoro` data.
 *
 * `origin: "review-2026"` throughout — this derivation is NOT thesis content,
 * even though every fact it produces is mechanically traceable to a 2010
 * source field (`sonoro.required`, `sonoro.distribuicao`). The *facts* are a
 * 2026 addition; only the *underlying data they read* is from 2010.
 */

import type { CommunicationalFacts, HistoricalMatrixPosition } from "./types";

export const POSITION_FACTS_ORIGIN = "review-2026" as const;

/**
 * Derive communicational facts for a historical matrix position.
 *
 * - soundFunction: "present" iff the position's `sonoro.required` is true —
 *   directly read from the 2010 data, not inferred.
 * - latency: "real-time" iff the position's distribuicao includes "VoIP"
 *   (the only 2010 distribution mode implying live, two-way, low-latency
 *   communication — e.g. positions 32/game-high and 33–35/mobile). Every
 *   other position, including those offering "ao vivo" (live) streaming
 *   alongside on-demand options, is treated as "non-critical": live
 *   broadcast tolerates ordinary streaming latency, unlike VoIP.
 * - fidelity: "standard" for every 2010 position — the thesis never
 *   specifies lossless-grade audio among its options (no FLAC-equivalent is
 *   listed at any position). Left as a single value rather than guessing a
 *   "high" tier from bit-rate qualifiers; revisit in a future 2026 review if
 *   finer distinction is wanted.
 */
export function deriveCommunicationalFacts(position: HistoricalMatrixPosition): CommunicationalFacts {
  const soundFunction: CommunicationalFacts["soundFunction"] = position.sonoro.required ? "present" : "none";

  const distribuicao = position.sonoro.distribuicao ?? [];
  const isRealTime = distribuicao.some((mode) => mode.includes("VoIP"));

  return {
    soundFunction,
    latency: isRealTime ? "real-time" : "non-critical",
    fidelity: "standard",
  };
}

/**
 * The methodology-layer statement (design D15; av-methodology-classification
 * spec: "The recommendation result explains the methodological chain before
 * showing resources"). Template-driven from the derived `soundFunction`
 * fact — never a per-position hard-coded string — so it stays true to
 * Cenário 1 ("o site não precisa conter nenhuma informação que envolva
 * elemento sonoro") and Cenário 2 ("transformar o mesmo conteúdo em conteúdo
 * audiovisual") without asserting anything beyond what the derived fact
 * supports.
 */
export function describeSoundIndication(facts: CommunicationalFacts): string {
  return facts.soundFunction === "none"
    ? "Esta posição da grade metodológica não demanda elementos sonoros — a configuração comunicacional da região (texto/imagem) não indica necessidade de som."
    : "A posição deste conteúdo associa sua configuração comunicacional ao uso de som — a região da grade em que ele se encontra indica potencialidade/necessidade sonora.";
}
